import type {
  FilterOperator,
  ReportDataSettingsV2,
  ReportFilterV2,
} from "./report-configuration.ts";
import type { ISODate } from "./dateUtils.ts";
import { todayUTC } from "./dateUtils.ts";
import {
  FIELD_BASES,
  DateFieldBase,
  NonDateFieldBase,
  FieldName,
} from "@/common/fields.ts";

const defaultDateStart = (): ISODate =>
  todayUTC().with({ month: 1, day: 1 }).toString() as ISODate;
const defaultDateEnd = (): ISODate => todayUTC().toString() as ISODate;

interface FilterData {
  filter: FilterName;
  defaultOperator: FilterOperator;
  defaultValue:
    | string
    | number
    | boolean
    | string[]
    | number[]
    | ISODate
    | (() => string | number | boolean | string[] | number[] | ISODate);
  defaultSecondaryValue?: number | string | (() => number | string); // For BETWEEN operations
  required?: boolean; // Whether the filter can be removed from a report
  description?: string; // for the LLM, overrides the default commonValues calculation when building the report config prompt
}

interface DateFilterData extends FilterData {
  supportsSinglePeriodView: boolean; // Whether the date filter supports a single period view, and thus, the compare period functionality.
}

type StandardFilterDefinition = NonDateFieldBase & FilterData;
export type DateFilterDefinition = DateFieldBase & DateFilterData;

export type FilterDefinition = StandardFilterDefinition | DateFilterDefinition;
export type FilterName = Exclude<
  FieldName,
  | "ANNUALIZED_TOTAL_CASH_COMPENSATION"
  | "APPLICATION_ID"
  | "APPLICANT_COUNT"
  | "ATTRITION_DRIVERS_IMPORTANCE"
  | "ATTRITION_PROBABILITY"
  | "BELONGING"
  | "BONUS_AMOUNT"
  | "COMMISSION_AMOUNT"
  | "DATE_OF_PROMOTION"
  | "EMPLOYEE_COUNT"
  | "ENGAGEMENT"
  | "EXPECTED_START_DATE"
  | "LAG_TIME_DAYS"
  | "PERFORMANCE_RATING"
  | "POSITIONS_IN_INDUSTRY_AND_OCCUPATION"
  | "SATISFACTION"
  | "TIME_TO_FILL_DRIVERS_IMPACT"
  | "TIME_TO_HIRE_DAYS"
  | "TOTAL_COMPENSATION"
>;

export const FILTERS: Record<FilterName, FilterDefinition> = {
  ACTION_TAKEN: {
    ...FIELD_BASES.ACTION_TAKEN,
    filter: "ACTION_TAKEN",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  ACTIVITY_DATE: {
    ...FIELD_BASES.ACTIVITY_DATE,
    filter: "ACTIVITY_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for when infractions occurred, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  AGE_GROUP: {
    ...FIELD_BASES.AGE_GROUP,
    filter: "AGE_GROUP",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  ANSWER_VALUE: {
    ...FIELD_BASES.ANSWER_VALUE,
    filter: "ANSWER_VALUE",
    defaultOperator: "LESS_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
  },
  APPLICANT_SOURCE_DETAIL: {
    ...FIELD_BASES.APPLICANT_SOURCE_DETAIL,
    filter: "APPLICANT_SOURCE_DETAIL",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  APPLICATION_DATE: {
    ...FIELD_BASES.APPLICATION_DATE,
    filter: "APPLICATION_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for job applications, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  APPLICANT_SOURCE: {
    ...FIELD_BASES.APPLICANT_SOURCE,
    filter: "APPLICANT_SOURCE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  ATTRITION_PROBABILITY_TIME_HORIZON: {
    // this will have special handling in the UI and backend.
    ...FIELD_BASES.ATTRITION_PROBABILITY_TIME_HORIZON,
    filter: "ATTRITION_PROBABILITY_TIME_HORIZON",
    defaultOperator: "SELECT",
    defaultValue: "attrition_6m",
    defaultSecondaryValue: undefined,
    description:
      "An enum of time frames for the attrition probability calculation, one of [attrition_3m, attrition_6m, attrition_12m, attrition_18m]",
  },
  BONUS_REASON: {
    ...FIELD_BASES.BONUS_REASON,
    filter: "BONUS_REASON",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  COMPLETION_CATEGORY: {
    ...FIELD_BASES.COMPLETION_CATEGORY,
    filter: "COMPLETION_CATEGORY",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  COMPLETION_VELOCITY_DAYS: {
    ...FIELD_BASES.COMPLETION_VELOCITY_DAYS,
    filter: "COMPLETION_VELOCITY_DAYS",
    defaultOperator: "LESS_THAN",
    defaultValue: 5,
    defaultSecondaryValue: undefined,
  },
  COMMISSION_REASON: {
    ...FIELD_BASES.COMMISSION_REASON,
    filter: "COMMISSION_REASON",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  COMPANY_ID: {
    ...FIELD_BASES.COMPANY_ID,
    filter: "COMPANY_ID",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  COMPANY_SIZE: {
    ...FIELD_BASES.COMPANY_SIZE,
    filter: "COMPANY_SIZE",
    defaultOperator: "EQUALS",
    defaultValue: ["All company sizes"],
    defaultSecondaryValue: undefined,
  },
  COUNTRY: {
    ...FIELD_BASES.COUNTRY,
    filter: "COUNTRY",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  DATE: {
    ...FIELD_BASES.DATE,
    filter: "DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for the report, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  DEPARTMENT: {
    ...FIELD_BASES.DEPARTMENT,
    filter: "DEPARTMENT",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  DISABILITY_STATUS: {
    ...FIELD_BASES.DISABILITY_STATUS,
    filter: "DISABILITY_STATUS",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  DISCIPLINARY_STATUS: {
    ...FIELD_BASES.DISCIPLINARY_STATUS,
    filter: "DISCIPLINARY_STATUS",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  DISCUSSION_DATE: {
    ...FIELD_BASES.DISCUSSION_DATE,
    filter: "DISCUSSION_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for discussions of infractions, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  DIVISION: {
    ...FIELD_BASES.DIVISION,
    filter: "DIVISION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  EDUCATION: {
    ...FIELD_BASES.EDUCATION,
    filter: "EDUCATION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  EMPLOYEE_COUNTY_NAME: {
    ...FIELD_BASES.EMPLOYEE_COUNTY_NAME,
    filter: "EMPLOYEE_COUNTY_NAME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  EMPLOYEE_GOAL_COMPLETION_PERCENT: {
    ...FIELD_BASES.EMPLOYEE_GOAL_COMPLETION_PERCENT,
    filter: "EMPLOYEE_GOAL_COMPLETION_PERCENT",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0.5,
    defaultSecondaryValue: undefined,
    description: "The percentage of an employee goal that has been completed.",
  },
  EMPLOYEE_GOAL_CREATED_DATE: {
    ...FIELD_BASES.EMPLOYEE_GOAL_CREATED_DATE,
    filter: "EMPLOYEE_GOAL_CREATED_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for when employee goals were created, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  EMPLOYEE_GOAL_COMPLETED_DATE: {
    ...FIELD_BASES.EMPLOYEE_GOAL_COMPLETED_DATE,
    filter: "EMPLOYEE_GOAL_COMPLETED_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for when employee goals were completed, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  EMPLOYEE_GOAL_DUE_DATE: {
    ...FIELD_BASES.EMPLOYEE_GOAL_DUE_DATE,
    filter: "EMPLOYEE_GOAL_DUE_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for when employee goals are due, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  EMPLOYEE_GOAL_TYPE: {
    ...FIELD_BASES.EMPLOYEE_GOAL_TYPE,
    filter: "EMPLOYEE_GOAL_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  EMPLOYEE_GOAL_STATUS: {
    ...FIELD_BASES.EMPLOYEE_GOAL_STATUS,
    filter: "EMPLOYEE_GOAL_STATUS",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  EMPLOYEE_MSA_NAME: {
    ...FIELD_BASES.EMPLOYEE_MSA_NAME,
    filter: "EMPLOYEE_MSA_NAME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  EMPLOYMENT_TYPE: {
    ...FIELD_BASES.EMPLOYMENT_TYPE,
    filter: "EMPLOYMENT_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  ETHNICITY: {
    ...FIELD_BASES.ETHNICITY,
    filter: "ETHNICITY",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  GENDER: {
    ...FIELD_BASES.GENDER,
    filter: "GENDER",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  GENERATION: {
    ...FIELD_BASES.GENERATION,
    filter: "GENERATION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  HIRE_DATE: {
    ...FIELD_BASES.HIRE_DATE,
    filter: "HIRE_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for employee hires, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: false,
  },
  HIRED_DATE: {
    ...FIELD_BASES.HIRED_DATE,
    filter: "HIRED_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for employee hires from applications, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  HIRING_MANAGER: {
    ...FIELD_BASES.HIRING_MANAGER,
    filter: "HIRING_MANAGER",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  INDUSTRY: {
    ...FIELD_BASES.INDUSTRY,
    filter: "INDUSTRY",
    defaultOperator: "EQUALS",
    defaultValue: ["All industries"],
    defaultSecondaryValue: undefined,
  },
  INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY: {
    ...FIELD_BASES.INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY,
    filter: "INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Year-over-year percentage change in the number of local jobs within this industry across all occupations.",
  },
  INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION: {
    ...FIELD_BASES.INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION,
    filter: "INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Share of all local jobs in this occupation that are located in this industry. The denominator is industry_context_occupation_total_positions.",
  },
  INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS: {
    ...FIELD_BASES.INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS,
    filter: "INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of jobs in the area within this occupation across all industries.",
  },
  INDUSTRY_SEPARATION_INTENSITY: {
    ...FIELD_BASES.INDUSTRY_SEPARATION_INTENSITY,
    filter: "INDUSTRY_SEPARATION_INTENSITY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
  },
  INDUSTRY_TOTAL_POSITIONS: {
    ...FIELD_BASES.INDUSTRY_TOTAL_POSITIONS,
    filter: "INDUSTRY_TOTAL_POSITIONS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
  },
  INFRACTION_DESCRIPTION: {
    ...FIELD_BASES.INFRACTION_DESCRIPTION,
    filter: "INFRACTION_DESCRIPTION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  INSIGHTS_16_TYPE: {
    ...FIELD_BASES.INSIGHTS_16_TYPE,
    filter: "INSIGHTS_16_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  INSIGHTS_8_TYPE: {
    ...FIELD_BASES.INSIGHTS_8_TYPE,
    filter: "INSIGHTS_8_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  INSIGHTS_OPPOSITE_8_TYPE: {
    ...FIELD_BASES.INSIGHTS_OPPOSITE_8_TYPE,
    filter: "INSIGHTS_OPPOSITE_8_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  INSIGHTS_72_TYPE: {
    ...FIELD_BASES.INSIGHTS_72_TYPE,
    filter: "INSIGHTS_72_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  INSIGHTS_LESS_CONSCIOUS_72_TYPE: {
    ...FIELD_BASES.INSIGHTS_LESS_CONSCIOUS_72_TYPE,
    filter: "INSIGHTS_LESS_CONSCIOUS_72_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  INSIGHTS_CONSCIOUS_BLUE_ENERGY: {
    ...FIELD_BASES.INSIGHTS_CONSCIOUS_BLUE_ENERGY,
    filter: "INSIGHTS_CONSCIOUS_BLUE_ENERGY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 3,
    defaultSecondaryValue: undefined,
    description:
      "The Blue score from the Insights Discovery assessment on a scale of 0-6.",
  },
  INSIGHTS_CONSCIOUS_GREEN_ENERGY: {
    ...FIELD_BASES.INSIGHTS_CONSCIOUS_GREEN_ENERGY,
    filter: "INSIGHTS_CONSCIOUS_GREEN_ENERGY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 3,
    defaultSecondaryValue: undefined,
    description:
      "The Green score from the Insights Discovery assessment on a scale of 0-6.",
  },
  INSIGHTS_CONSCIOUS_RED_ENERGY: {
    ...FIELD_BASES.INSIGHTS_CONSCIOUS_RED_ENERGY,
    filter: "INSIGHTS_CONSCIOUS_RED_ENERGY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 3,
    defaultSecondaryValue: undefined,
    description:
      "The Red score from the Insights Discovery assessment on a scale of 0-6.",
  },
  INSIGHTS_CONSCIOUS_YELLOW_ENERGY: {
    ...FIELD_BASES.INSIGHTS_CONSCIOUS_YELLOW_ENERGY,
    filter: "INSIGHTS_CONSCIOUS_YELLOW_ENERGY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 3,
    defaultSecondaryValue: undefined,
    description:
      "The Yellow score from the Insights Discovery assessment on a scale of 0-6.",
  },
  INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY: {
    ...FIELD_BASES.INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY,
    filter: "INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 3,
    defaultSecondaryValue: undefined,
    description:
      "The BlueLC (less conscious Blue) score from the Insights Discovery assessment on a scale of 0-6.",
  },
  INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY: {
    ...FIELD_BASES.INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY,
    filter: "INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 3,
    defaultSecondaryValue: undefined,
    description:
      "The GreenLC (less conscious Green) score from the Insights Discovery assessment on a scale of 0-6.",
  },
  INSIGHTS_LESS_CONSCIOUS_RED_ENERGY: {
    ...FIELD_BASES.INSIGHTS_LESS_CONSCIOUS_RED_ENERGY,
    filter: "INSIGHTS_LESS_CONSCIOUS_RED_ENERGY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 3,
    defaultSecondaryValue: undefined,
    description:
      "The RedLC (less conscious Red) score from the Insights Discovery assessment on a scale of 0-6.",
  },
  INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY: {
    ...FIELD_BASES.INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY,
    filter: "INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 3,
    defaultSecondaryValue: undefined,
    description:
      "The YellowLC (less conscious Yellow) score from the Insights Discovery assessment on a scale of 0-6.",
  },
  INSIGHTS_LEADING_CONSCIOUS_COLOR: {
    ...FIELD_BASES.INSIGHTS_LEADING_CONSCIOUS_COLOR,
    filter: "INSIGHTS_LEADING_CONSCIOUS_COLOR",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
    description:
      "The highest-value color (Fiery Red, Cool Blue, Earth Green, Sunshine Yellow) from the Insights Discovery assessment for the employee.",
  },
  INSIGHTS_SUPERVISOR_SAME_8_TYPE: {
    ...FIELD_BASES.INSIGHTS_SUPERVISOR_SAME_8_TYPE,
    filter: "INSIGHTS_SUPERVISOR_SAME_8_TYPE",
    defaultOperator: "EQUALS",
    defaultValue: "Yes",
    defaultSecondaryValue: undefined,
    description:
      "A 'Yes'/'No' string value indicating whether the Insights Discovery core type (persona) for the employee's supervisor is the same as the employee's primary type.",
  },
  INSIGHTS_SUPERVISOR_OPPOSITE_8_TYPE: {
    ...FIELD_BASES.INSIGHTS_SUPERVISOR_OPPOSITE_8_TYPE,
    filter: "INSIGHTS_SUPERVISOR_OPPOSITE_8_TYPE",
    defaultOperator: "EQUALS",
    defaultValue: "Yes",
    defaultSecondaryValue: undefined,
    description:
      "A 'Yes'/'No' string value indicating whether the Insights Discovery core type (persona) for the employee's supervisor is opposite the employee's primary type.",
  },
  LAG_TIME_BUCKET: {
    ...FIELD_BASES.LAG_TIME_BUCKET,
    filter: "LAG_TIME_BUCKET",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  JOB_BAND: {
    ...FIELD_BASES.JOB_BAND,
    filter: "JOB_BAND",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  JOB_CATEGORY: {
    ...FIELD_BASES.JOB_CATEGORY,
    filter: "JOB_CATEGORY",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  JOB_FAMILY: {
    ...FIELD_BASES.JOB_FAMILY,
    filter: "JOB_FAMILY",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  JOB_HIERARCHY: {
    ...FIELD_BASES.JOB_HIERARCHY,
    filter: "JOB_HIERARCHY",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  JOB_REQ_ACCEPTANCE_RATE: {
    ...FIELD_BASES.JOB_REQ_ACCEPTANCE_RATE,
    filter: "JOB_REQ_ACCEPTANCE_RATE",
    defaultOperator: "GREATER_THAN",
    defaultValue: 50,
    description:
      "The job requisition acceptance rate, specified as a percentage.",
  },
  JOB_REQ_FILL_DATE: {
    ...FIELD_BASES.JOB_REQ_FILL_DATE,
    filter: "JOB_REQ_FILL_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for job requisition fills, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  JOB_REQ_ID: {
    ...FIELD_BASES.JOB_REQ_ID,
    filter: "JOB_REQ_ID",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
    description: "The unique identifier for a job requisition.",
  },
  JOB_REQ_OPEN_DATE: {
    ...FIELD_BASES.JOB_REQ_OPEN_DATE,
    filter: "JOB_REQ_OPEN_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for job requisitions, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  JOB_REQ_STATUS: {
    ...FIELD_BASES.JOB_REQ_STATUS,
    filter: "JOB_REQ_STATUS",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  JOB_TITLE: {
    ...FIELD_BASES.JOB_TITLE,
    filter: "JOB_TITLE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO: {
    ...FIELD_BASES.LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO,
    filter: "LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Current unemployed workers divided by current employed workers in the area. Measures the number of unemployed people available relative to employed people; it is not the unemployment rate, which uses the labor force as its denominator.",
  },
  LABOR_AVAILABILITY_EMPLOYED_WORKERS: {
    ...FIELD_BASES.LABOR_AVAILABILITY_EMPLOYED_WORKERS,
    filter: "LABOR_AVAILABILITY_EMPLOYED_WORKERS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of people in the area who are currently employed.",
  },
  LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE: {
    ...FIELD_BASES.LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE,
    filter: "LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Relative difference between current labor availability and its own trailing-12-month average. Positive values indicate that current availability is higher than usual; negative values indicate that it is lower than usual.",
  },
  LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY: {
    ...FIELD_BASES.LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY,
    filter: "LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Local current labor availability ratio minus the national current labor availability ratio. Positive values indicate more unemployed people relative to employed people locally than nationally.",
  },
  LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO: {
    ...FIELD_BASES.LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO,
    filter: "LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Current unemployed workers divided by current employed workers across the country. Provides the national benchmark for the local labor availability ratio.",
  },
  LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS: {
    ...FIELD_BASES.LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS,
    filter: "LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Average number of employed people in the area over the trailing 12 months. Smooths monthly or seasonal variation.",
  },
  LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO: {
    ...FIELD_BASES.LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO,
    filter: "LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Trailing-12-month average unemployed workers divided by trailing-12-month average employed workers in the area. Measures smoothed labor availability relative to employment; it is not the unemployment rate.",
  },
  LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS: {
    ...FIELD_BASES.LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS,
    filter: "LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Average number of unemployed people in the area over the trailing 12 months. Smooths monthly or seasonal variation.",
  },
  LABOR_AVAILABILITY_UNEMPLOYED_WORKERS: {
    ...FIELD_BASES.LABOR_AVAILABILITY_UNEMPLOYED_WORKERS,
    filter: "LABOR_AVAILABILITY_UNEMPLOYED_WORKERS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of people in the area who are unemployed and actively looking for work.",
  },
  LGBTQ_STATUS: {
    ...FIELD_BASES.LGBTQ_STATUS,
    filter: "LGBTQ_STATUS",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  LOCATION: {
    ...FIELD_BASES.LOCATION,
    filter: "LOCATION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  MARKET_PRESSURE_ROLE: {
    ...FIELD_BASES.MARKET_PRESSURE_ROLE,
    filter: "MARKET_PRESSURE_ROLE",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    description:
      "Index for the employee’s SOC in the employer’s area in the range 0-10; it measures occupational market conditions, not individual employee risk.",
  },
  MARKET_PRESSURE_MARKET: {
    ...FIELD_BASES.MARKET_PRESSURE_MARKET,
    filter: "MARKET_PRESSURE_MARKET",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    description:
      "A 0–10 scale comparing local versus national labor supply, hiring, employment growth and wage growth across all occupations for that specific geo_level; higher values indicate greater market pressure",
  },
  NAICS_NAME: {
    ...FIELD_BASES.NAICS_NAME,
    filter: "NAICS_NAME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  OCCUPATION_MARKET_EMP_CHANGE_YOY: {
    ...FIELD_BASES.OCCUPATION_MARKET_EMP_CHANGE_YOY,
    filter: "OCCUPATION_MARKET_EMP_CHANGE_YOY",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Year-over-year percentage change in occupied positions for this occupation in the area. Positive values indicate more local jobs than the previous year; negative values indicate fewer.",
  },
  OCCUPATION_MARKET_GROWTH_OPENINGS: {
    ...FIELD_BASES.OCCUPATION_MARKET_GROWTH_OPENINGS,
    filter: "OCCUPATION_MARKET_GROWTH_OPENINGS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of job openings created by net growth in this occupation in the area during the period. These openings result from an increase in the number of jobs, not from replacing workers who leave.",
  },
  OCCUPATION_MARKET_HIRES: {
    ...FIELD_BASES.OCCUPATION_MARKET_HIRES,
    filter: "OCCUPATION_MARKET_HIRES",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of times jobs in this occupation were started in the area during the year. A person may contribute more than one hire.",
  },
  OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM: {
    ...FIELD_BASES.OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM,
    filter: "OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Estimated difference between local and national earnings for this occupation, expressed as a fraction of national earnings. A value of 0.10 means local earnings are approximately 10% higher than the national benchmark; a negative value means lower earnings.",
  },
  OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE: {
    ...FIELD_BASES.OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE,
    filter: "OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated replacement openings divided by occupied positions for this occupation in the area. Measures the annual hiring required to replace workers who leave, excluding openings created by net job growth.",
  },
  OCCUPATION_MARKET_REPLACEMENT_OPENINGS: {
    ...FIELD_BASES.OCCUPATION_MARKET_REPLACEMENT_OPENINGS,
    filter: "OCCUPATION_MARKET_REPLACEMENT_OPENINGS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of openings in this occupation required to replace workers who left jobs in the area during the year. These openings maintain the existing number of jobs and exclude openings created by net job growth.",
  },
  OCCUPATION_MARKET_RESIDENT_EMPLOYMENT: {
    ...FIELD_BASES.OCCUPATION_MARKET_RESIDENT_EMPLOYMENT,
    filter: "OCCUPATION_MARKET_RESIDENT_EMPLOYMENT",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of people who live in the area and work in this occupation, regardless of where their jobs are located. Unlike occupied positions, this counts resident workers rather than local jobs.",
  },
  OCCUPATION_MARKET_SEPARATIONS: {
    ...FIELD_BASES.OCCUPATION_MARKET_SEPARATIONS,
    filter: "OCCUPATION_MARKET_SEPARATIONS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of times jobs in this occupation ended in the area during the year. Includes all exits, including moves to another job; it is not limited to resignations or permanent exits from employment.",
  },
  OCCUPATION_MARKET_YOY_HIRES_CHANGE: {
    ...FIELD_BASES.OCCUPATION_MARKET_YOY_HIRES_CHANGE,
    filter: "OCCUPATION_MARKET_YOY_HIRES_CHANGE",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Year-over-year percentage change in hires for this occupation in the area. Measures how local hiring volume changed compared with the previous year.",
  },
  OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE: {
    ...FIELD_BASES.OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE,
    filter: "OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Year-over-year percentage change in replacement openings for this occupation in the area. Measures how replacement demand changed compared with the previous year.",
  },
  OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE: {
    ...FIELD_BASES.OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE,
    filter: "OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Year-over-year percentage change in separations for this occupation in the area. Measures how the number of job exits changed compared with the previous year.",
  },
  OCCUPATION_LOCAL_HIRE_RATE: {
    ...FIELD_BASES.OCCUPATION_LOCAL_HIRE_RATE,
    filter: "OCCUPATION_LOCAL_HIRE_RATE",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    description:
      "The occupation local hire rate, specified as a rate of hires per job per year in the employee's area on a scale of 0 to 1.",
  },
  OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE: {
    ...FIELD_BASES.OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE,
    filter: "OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    description:
      "Local hire rate minus national for this employee's role. Positive means this area hires into the role faster than the country does.",
  },
  OCCUPATION_NATIONAL_HIRE_RATE: {
    ...FIELD_BASES.OCCUPATION_NATIONAL_HIRE_RATE,
    filter: "OCCUPATION_NATIONAL_HIRE_RATE",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    description:
      "The occupation national hire rate, specified as a rate of hires per job per year on a scale of 0 to 1.",
  },
  OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE: {
    ...FIELD_BASES.OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE,
    filter: "OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    description:
      "The net employment growth rate for this employee's role in this area. Positive means the number of jobs is increasing, negative means it is decreasing.",
  },
  OCCUPATION_OCCUPIED_POSITIONS: {
    ...FIELD_BASES.OCCUPATION_OCCUPIED_POSITIONS,
    filter: "OCCUPATION_OCCUPIED_POSITIONS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    description:
      "How many jobs of this kind exist in the area - the size of the local pool, and the denominator under the role rates. A modelled estimate, not a headcount.",
  },
  OCCUPATION_SEPARATION_INTENSITY: {
    ...FIELD_BASES.OCCUPATION_SEPARATION_INTENSITY,
    filter: "OCCUPATION_SEPARATION_INTENSITY",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    description:
      "Exits per job per year for this ROLE in this area. 0.80 means roughly 80 people left for every 100 such jobs. Counts every exit, not just resignations, and it is the market's rate - not this employer's.",
  },
  ORIGIN: {
    ...FIELD_BASES.ORIGIN,
    filter: "ORIGIN",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  OSHA_INCIDENT_DATE: {
    ...FIELD_BASES.OSHA_INCIDENT_DATE,
    filter: "OSHA_INCIDENT_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for OSHA incidents, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  OSHA_INCIDENT_TYPE: {
    ...FIELD_BASES.OSHA_INCIDENT_TYPE,
    filter: "OSHA_INCIDENT_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  OSHA_AFFECTED_BODY_PART: {
    ...FIELD_BASES.OSHA_AFFECTED_BODY_PART,
    filter: "OSHA_AFFECTED_BODY_PART",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  OSHA_FIRST_AID_ONLY: {
    ...FIELD_BASES.OSHA_FIRST_AID_ONLY,
    filter: "OSHA_FIRST_AID_ONLY",
    defaultOperator: "EQUALS",
    defaultValue: ["N"],
    defaultSecondaryValue: undefined,
  },
  OSHA_ON_COMPANY_PROPERTY: {
    ...FIELD_BASES.OSHA_ON_COMPANY_PROPERTY,
    filter: "OSHA_ON_COMPANY_PROPERTY",
    defaultOperator: "EQUALS",
    defaultValue: ["Y"],
    defaultSecondaryValue: undefined,
  },
  OSHA_OCCUPATION: {
    ...FIELD_BASES.OSHA_OCCUPATION,
    filter: "OSHA_OCCUPATION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  OSHA_ESTABLISHMENT_NAME: {
    ...FIELD_BASES.OSHA_ESTABLISHMENT_NAME,
    filter: "OSHA_ESTABLISHMENT_NAME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  PAY_BAND: {
    ...FIELD_BASES.PAY_BAND,
    filter: "PAY_BAND",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  PRIMARY_RECRUITER: {
    ...FIELD_BASES.PRIMARY_RECRUITER,
    filter: "PRIMARY_RECRUITER",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  PRIMARY_WORK_LOCATION: {
    ...FIELD_BASES.PRIMARY_WORK_LOCATION,
    filter: "PRIMARY_WORK_LOCATION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  QUESTION_LABEL: {
    ...FIELD_BASES.QUESTION_LABEL,
    filter: "QUESTION_LABEL",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  QUESTION_SUBTHEME: {
    ...FIELD_BASES.QUESTION_SUBTHEME,
    filter: "QUESTION_SUBTHEME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  QUESTION_THEME: {
    ...FIELD_BASES.QUESTION_THEME,
    filter: "QUESTION_THEME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  RACE: {
    ...FIELD_BASES.RACE,
    filter: "RACE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  RACE_GROUPING: {
    ...FIELD_BASES.RACE_GROUPING,
    filter: "RACE_GROUPING",
    defaultOperator: "EQUALS",
    defaultValue: ["Show All Races"],
    defaultSecondaryValue: undefined,
    required: true,
  },
  RECRUITMENT_STAGE: {
    ...FIELD_BASES.RECRUITMENT_STAGE,
    filter: "RECRUITMENT_STAGE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  RECRUITMENT_STAGE_HISTORICAL: {
    ...FIELD_BASES.RECRUITMENT_STAGE_HISTORICAL,
    filter: "RECRUITMENT_STAGE_HISTORICAL",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  REGION: {
    ...FIELD_BASES.REGION,
    filter: "REGION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  REGRETTABLE_ATTRITION: {
    ...FIELD_BASES.REGRETTABLE_ATTRITION,
    filter: "REGRETTABLE_ATTRITION",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT: {
    ...FIELD_BASES.SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT,
    filter: "SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Share of all unemployed people in the area whose most recent job was in this sector. The denominator is total area unemployment.",
  },
  SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE: {
    ...FIELD_BASES.SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE,
    filter: "SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Average number of unemployed people over the trailing 12 months whose most recent job was in this sector.",
  },
  SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY: {
    ...FIELD_BASES.SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY,
    filter: "SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY",
    defaultOperator: "BETWEEN",
    defaultValue: -1,
    defaultSecondaryValue: 1,
    description:
      "Year-over-year percentage change in the number of unemployed people in the area whose most recent job was in this sector.",
  },
  SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS: {
    ...FIELD_BASES.SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS,
    filter: "SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS",
    defaultOperator: "GREATER_THAN",
    defaultValue: 0,
    defaultSecondaryValue: undefined,
    description:
      "Estimated number of unemployed people in the area whose most recent job was in this sector. This is a subset of total local unemployed workers, classified by previous sector.",
  },
  SOC_TITLE: {
    ...FIELD_BASES.SOC_TITLE,
    filter: "SOC_TITLE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  STATE: {
    ...FIELD_BASES.STATE,
    filter: "STATE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  STORE_NAME: {
    ...FIELD_BASES.STORE_NAME,
    filter: "STORE_NAME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  STORE_STATUS: {
    ...FIELD_BASES.STORE_STATUS,
    filter: "STORE_STATUS",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  SUBMIT_DATE: {
    ...FIELD_BASES.SUBMIT_DATE,
    filter: "SUBMIT_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for survey submission dates, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: true,
  },
  SUPERVISOR_ID: {
    ...FIELD_BASES.SUPERVISOR_ID,
    filter: "SUPERVISOR_ID",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
    description: "The unique identifier for a supervisor.",
  },
  SUPERVISOR_JOB_TITLE: {
    ...FIELD_BASES.SUPERVISOR_JOB_TITLE,
    filter: "SUPERVISOR_JOB_TITLE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  SUPERVISOR_NAME: {
    ...FIELD_BASES.SUPERVISOR_NAME,
    filter: "SUPERVISOR_NAME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  SURVEY_NAME: {
    ...FIELD_BASES.SURVEY_NAME,
    filter: "SURVEY_NAME",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  SURVEY_STATUS: {
    ...FIELD_BASES.SURVEY_STATUS,
    filter: "SURVEY_STATUS",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  TENURE_GROUP: {
    ...FIELD_BASES.TENURE_GROUP,
    filter: "TENURE_GROUP",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  TERMINATION_DATE: {
    ...FIELD_BASES.TERMINATION_DATE,
    filter: "TERMINATION_DATE",
    defaultOperator: "BETWEEN",
    defaultValue: defaultDateStart,
    defaultSecondaryValue: defaultDateEnd,
    description:
      "The date range for employee terminations, specified as a start and end date in SQL format YYYY-MM-DD.",
    supportsSinglePeriodView: false,
  },
  TERMINATION_REASON: {
    ...FIELD_BASES.TERMINATION_REASON,
    filter: "TERMINATION_REASON",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  TERMINATION_TYPE: {
    ...FIELD_BASES.TERMINATION_TYPE,
    filter: "TERMINATION_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  TIME_TO_FILL_DAYS: {
    ...FIELD_BASES.TIME_TO_FILL_DAYS,
    filter: "TIME_TO_FILL_DAYS",
    defaultOperator: "BETWEEN",
    defaultValue: 0,
    defaultSecondaryValue: 90,
    description:
      "The number of days between when the job requisition was opened and when it was filled.",
  },
  VETERAN_STATUS: {
    ...FIELD_BASES.VETERAN_STATUS,
    filter: "VETERAN_STATUS",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
  WORK_ARRANGEMENT_TYPE: {
    ...FIELD_BASES.WORK_ARRANGEMENT_TYPE,
    filter: "WORK_ARRANGEMENT_TYPE",
    defaultOperator: "IN",
    defaultValue: [],
    defaultSecondaryValue: undefined,
  },
};

export const ReportFilterV2FromFilterDefinition = (
  filterDef: FilterDefinition,
): ReportFilterV2 => {
  return {
    filterDefinitionName: filterDef.filter,
    operator: filterDef.defaultOperator,
    value:
      typeof filterDef.defaultValue === "function"
        ? filterDef.defaultValue()
        : filterDef.defaultValue,
    secondaryValue:
      typeof filterDef.defaultSecondaryValue === "function"
        ? filterDef.defaultSecondaryValue()
        : filterDef.defaultSecondaryValue,
  };
};

// Finds the first filter present on a field with a date type
export const findTimeFilterV2 = (
  data: ReportDataSettingsV2,
): ReportFilterV2 | null => {
  let foundFilterDef: ReportFilterV2 | null = null;
  Object.values(data.filters).forEach((filterDef) => {
    if (
      FIELD_BASES[filterDef.filterDefinitionName].type === "date" &&
      !foundFilterDef
    ) {
      foundFilterDef = filterDef;
    }
  });
  return foundFilterDef;
};

export const addTimeFilterByDataset = (config: {
  data: {
    datasetName: ReportDataSettingsV2["datasetName"];
    filters: ReportFilterV2[];
  };
}) => {
  const newTimeFilterDef = Object.values(FILTERS).find(
    (filterDef) =>
      filterDef.type === "date" &&
      filterDef.datasetName.includes(config.data.datasetName),
  );
  if (newTimeFilterDef) {
    config.data.filters.push({
      filterDefinitionName: newTimeFilterDef.filter,
      operator: "LAST_365_DAYS",
      value: [],
    });
  }
};
